import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Copy } from "lucide-react";

import { ErrorConsulta, EsqueletoTabla } from "../componentes/Errores.tsx";
import { Insignia, Seccion, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";
import { usarCargasRechazadas } from "../ganchos/datos.ts";
import { fechaConHora } from "../formato.ts";
import type { CargaRechazada } from "../api/tipos.ts";

/**
 * Lo que el sistema del cliente mandó y no se pudo guardar (SPEC-019 §8 bis).
 *
 * POR QUÉ NO ES UNA LISTA A SECAS: si un ERP manda mal el formato de fecha,
 * falla en las cuatrocientas filas del mes. Cuatrocientas tarjetas idénticas no
 * son información, son ruido, y el dueño acabaría cerrando la pantalla.
 *
 * Lo que esa persona necesita saber es otra cosa: "tu sistema manda las fechas
 * en un formato que no acepto, 35 veces desde el martes". Por eso se agrupa por
 * motivo, con el detalle plegado debajo.
 *
 * Y necesita poder ENSEÑÁRSELO a quien escribió la integración, que no es él.
 * De ahí el botón de copiar: se lleva el motivo y la fila tal como llegó.
 */
export function CargasRechazadas() {
  const { data, isPending, error, refetch } = usarCargasRechazadas();
  const rechazos = data?.rechazos ?? [];

  const grupos = useMemo(() => agrupar(rechazos), [rechazos]);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  return (
    <Seccion
      titulo="Cargas rechazadas"
      descripcion="Lo que el sistema conectado a tu cuenta intentó cargar y no se pudo guardar."
    >
      {isPending ? (
        <Tarjeta className="p-4">
          <EsqueletoTabla filas={3} />
        </Tarjeta>
      ) : rechazos.length === 0 ? (
        <Tarjeta>
          <Vacio icono={CheckCircle2} titulo="Nada rechazado">
            Todo lo que ha entrado por la API se guardó. Si echas en falta una factura o un abono,
            el problema no está aquí: puede que tu sistema no la haya enviado todavía.
          </Vacio>
        </Tarjeta>
      ) : (
        <>
          {/* El resumen antes del detalle: casi siempre son muchas filas con el
              mismo problema, y saber CUÁNTAS y DESDE CUÁNDO es lo que permite
              decir "esto empezó el martes" a quien puede arreglarlo. */}
          <p className="max-w-prose text-sm leading-relaxed text-tenue">
            {rechazos.length} fila(s) rechazadas, por {grupos.length} motivo(s) distinto(s). Esto lo
            manda el sistema del cliente, no nosotros: enséñaselo a quien lo mantiene.
          </p>
          <ul className="space-y-3">
            {grupos.map((g) => (
              <Grupo key={g.codigo} grupo={g} />
            ))}
          </ul>
        </>
      )}
    </Seccion>
  );
}

interface GrupoRechazo {
  codigo: string;
  detalle: string;
  campo: string;
  filas: CargaRechazada[];
  desde: string;
}

/** Agrupa por el CÓDIGO, que es lo estable; el detalle solo ilustra. */
function agrupar(rechazos: CargaRechazada[]): GrupoRechazo[] {
  const por = new Map<string, GrupoRechazo>();
  for (const r of rechazos) {
    for (const p of r.problemas) {
      const g = por.get(p.codigo) ?? {
        codigo: p.codigo, detalle: p.detalle, campo: p.campo, filas: [], desde: r.recibido_en,
      };
      if (!g.filas.includes(r)) g.filas.push(r);
      if (r.recibido_en < g.desde) g.desde = r.recibido_en;
      por.set(p.codigo, g);
    }
  }
  return [...por.values()].sort((a, b) => b.filas.length - a.filas.length);
}

function Grupo({ grupo }: { grupo: GrupoRechazo }) {
  const [abierto, setAbierto] = useState(false);
  const muestra = grupo.filas.slice(0, 5);

  return (
    <li>
      <Tarjeta className="overflow-hidden">
        <button
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-superficie-2"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-riesgo-tinta" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{grupo.filas.length} fila(s)</span>
              <Insignia tono="neutro">{grupo.campo || "sin campo"}</Insignia>
              <span className="text-xs text-tenue-2">desde {fechaConHora(grupo.desde)}</span>
            </span>
            <span className="mt-1 block max-w-prose text-sm leading-relaxed text-tenue">
              {grupo.detalle}
            </span>
          </span>
          <ChevronDown
            className={cx("mt-0.5 size-4 shrink-0 text-tenue transition-transform", abierto && "rotate-180")}
            aria-hidden
          />
        </button>

        {abierto && (
          <div className="space-y-3 border-t border-borde bg-superficie-2/50 p-4">
            {muestra.map((r) => (
              <Fila key={r.id} rechazo={r} />
            ))}
            {grupo.filas.length > muestra.length && (
              <p className="text-xs text-tenue-2">
                y {grupo.filas.length - muestra.length} más con el mismo motivo.
              </p>
            )}
          </div>
        )}
      </Tarjeta>
    </li>
  );
}

function Fila({ rechazo }: { rechazo: CargaRechazada }) {
  const [copiado, setCopiado] = useState(false);
  const texto = JSON.stringify(
    { recibido: rechazo.recibido_en, entidad: rechazo.entidad, problemas: rechazo.problemas, fila: rechazo.fila },
    null,
    2,
  );

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin portapapeles —contexto no seguro, permiso denegado— el texto sigue
      // ahí para seleccionarlo a mano. No se avisa de un fallo que no impide nada.
    }
  };

  return (
    <div className="rounded-xl border border-borde bg-superficie p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-tenue">
        <Insignia tono="neutro">{rechazo.entidad}</Insignia>
        {rechazo.credencial && (
          <span className="cifra" title="Credencial que hizo la carga">
            {rechazo.credencial}
          </span>
        )}
        <span>{fechaConHora(rechazo.recibido_en)}</span>
        <button
          onClick={() => void copiar()}
          className="ml-auto flex items-center gap-1.5 rounded-lg px-2 py-1 font-medium text-tenue transition-colors hover:bg-superficie-2 hover:text-tinta"
        >
          <Copy className="size-3.5" aria-hidden />
          {copiado ? "Copiado" : "Copiar para tu proveedor"}
        </button>
      </div>
      {/* La fila tal como llegó. Es lo que hace accionable la pantalla: sin
          esto, "algo falló" no se puede depurar con nadie. */}
      <pre className="overflow-x-auto rounded-lg bg-superficie-2 p-2.5 text-xs leading-relaxed text-tenue">
        {JSON.stringify(rechazo.fila, null, 2)}
      </pre>
    </div>
  );
}
